// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
/**
 * Dado de leitura das solicitações DTP ↔ SME.
 *
 * A tabela `sme_demandas` existe (`db/migrations/0002_dominio_base.sql`), mas
 * não há módulo que a sirva em `apps/api/src/modulos/`. O mock entra sob a
 * exceção registrada em `CLAUDE.md` para `/sme/demandas` — só leitura, dado
 * claramente fictício, e some antes de qualquer promoção `develop` → `hml`
 * (`git grep -n "TEMPORÁRIO" apps/web/src`, checklist de `docs/ALVOS.md`).
 *
 * NADA AQUI GRAVA, nem em memória. Criar, cancelar, encaminhar e responder
 * mostram o aviso de indisponibilidade na tela; não existe mutação neste
 * arquivo, de propósito.
 *
 * AS DATAS SÃO RELATIVAS AO RELÓGIO DA EXECUÇÃO. A tela ordena por prazo e
 * pinta SLA vencido e vencendo: data gravada à mão envelhece, e um dia toda
 * linha aparece vencida — ou a tela esvazia sob o filtro de período.
 */

import { useQuery } from '@tanstack/react-query';

import { MOCK_DRES } from '../dados-simulados';
import type {
  EscolaDaDre,
  EventoSolicitacaoSme,
  FiltroSolicitacoesSme,
  OrigemSolicitacaoSme,
  SolicitacaoSmeDetalhe,
  SolicitacaoSmeLinha,
  StatusSolicitacaoSme,
  TipoSolicitacaoSme,
} from './tipos';

const DIA_MS = 86_400_000;

/** Instante ISO de `dias` atrás (fração permitida: 0.5 = 12 h). */
function diasAtras(dias: number): string {
  return new Date(Date.now() - dias * DIA_MS).toISOString();
}

/** Data `yyyy-mm-dd` deslocada `dias` a partir de hoje, no fuso local. */
function dataDaqui(dias: number): string {
  const d = new Date(Date.now() + dias * DIA_MS);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nomeDaDre(dreId: string): string {
  const dre = MOCK_DRES.find((d) => d.id === dreId);
  if (!dre?.nome) throw new Error(`DRE simulada ${dreId} não existe em MOCK_DRES.`);
  return dre.nome;
}

/** Três escolas fictícias por DRE — nome que não confunde com unidade real. */
const ESCOLAS: EscolaDaDre[] = MOCK_DRES.flatMap((dre, i) =>
  ['A', 'B', 'C'].map((letra, j) => ({
    id: `esc-${dre.id}-${letra.toLowerCase()}`,
    nome: `EMEF Fictícia ${dre.codigo ?? i + 1}-${letra}`,
    codigoEol: String(900_000 + i * 10 + j),
    bairro: `Bairro Exemplo ${j + 1}`,
  })),
);

const DRE_DA_ESCOLA = new Map<string, string>(
  MOCK_DRES.flatMap((dre) => ['a', 'b', 'c'].map((l) => [`esc-${dre.id}-${l}`, dre.id] as const)),
);

interface Semente {
  id: string;
  numero: number;
  origem: OrigemSolicitacaoSme;
  tipo: TipoSolicitacaoSme;
  titulo: string;
  justificativa: string;
  dreId: string;
  escolaId?: string;
  status: StatusSolicitacaoSme;
  /** Dias desde a criação. */
  criadaHa: number;
  /** Prazo desejado em dias a partir de hoje (negativo = já passou). */
  prazoEmDias?: number;
  encaminhamentoDre?: string;
  respostaDre?: string;
  respostaFinal?: string;
  /** Trilha além do evento de criação, em dias atrás. */
  eventos?: { acao: string; ha: number; detalhe?: string }[];
}

/**
 * Doze solicitações, cobrindo os dois sentidos, os sete estados, SLA vencido,
 * vencendo e folgado, escola informada e não, um pedido de complemento
 * pendente e o triângulo SME ↔ DRE nos três pontos (a encaminhar,
 * encaminhado, respondido).
 */
const SEMENTES: Semente[] = [
  {
    id: 'sol-001',
    numero: 412,
    origem: 'DTP',
    tipo: 'QUESTIONAMENTO_OS',
    titulo: 'Divergência de quilometragem cadastrada na OS 1002',
    justificativa:
      'A OS 1002 aparece com quilometragem base diferente da rota vigente. Pedimos que a SME confirme com a DRE qual é o trajeto atendido hoje.',
    dreId: 'dre-2',
    status: 'EM_ANALISE',
    criadaHa: 3,
    prazoEmDias: 1,
    eventos: [{ acao: 'ANALISAR', ha: 2 }],
  },
  {
    id: 'sol-002',
    numero: 413,
    origem: 'SME',
    tipo: 'INCLUSAO_ALUNO',
    titulo: 'Inclusão de dois alunos com deficiência na rota da manhã',
    justificativa:
      'Dois estudantes matriculados neste bimestre precisam de transporte adaptado no período da manhã. A unidade já confirmou o endereço de embarque.',
    dreId: 'dre-1',
    escolaId: 'esc-dre-1-a',
    status: 'ABERTA',
    criadaHa: 9,
    prazoEmDias: -2,
  },
  {
    id: 'sol-003',
    numero: 414,
    origem: 'SME',
    tipo: 'ALTERACAO_ITINERARIO',
    titulo: 'Alteração de itinerário por obra na via de acesso',
    justificativa:
      'A via de acesso principal da unidade ficará interditada por obra durante o semestre. Solicitamos que a DTP avalie o desvio proposto pela escola.',
    dreId: 'dre-10',
    escolaId: 'esc-dre-10-b',
    status: 'EM_ANALISE',
    criadaHa: 6,
    prazoEmDias: 4,
    eventos: [
      { acao: 'ANALISAR', ha: 5 },
      {
        acao: 'PEDIR_COMPLEMENTO',
        ha: 1,
        detalhe: 'Enviem o croqui do desvio com os pontos de embarque que mudam.',
      },
    ],
  },
  {
    id: 'sol-004',
    numero: 415,
    origem: 'DTP',
    tipo: 'ESCALONAMENTO_APONTAMENTO',
    titulo: 'Apontamento do mês anterior sem retorno da DRE',
    justificativa:
      'A DRE ainda não concluiu o apontamento do mês anterior. Solicitamos retorno para viabilizar o fechamento no prazo.',
    dreId: 'dre-13',
    status: 'ABERTA',
    criadaHa: 1,
  },
  {
    id: 'sol-005',
    numero: 416,
    origem: 'DTP',
    tipo: 'QUESTIONAMENTO_OS',
    titulo: 'Confirmação de dias letivos suspensos na OS 2890',
    justificativa:
      'A OS 2890 registra operação em dias que o calendário da unidade marca como suspensos. Precisamos da confirmação antes da apuração.',
    dreId: 'dre-11',
    status: 'ENCAMINHADA_DRE',
    criadaHa: 5,
    prazoEmDias: 3,
    encaminhamentoDre:
      'Verifiquem os dias letivos do período e confirmem se houve suspensão de aulas na unidade atendida pela OS 2890.',
    eventos: [
      { acao: 'ANALISAR', ha: 4 },
      { acao: 'ENCAMINHAR_DRE', ha: 3 },
    ],
  },
  {
    id: 'sol-006',
    numero: 417,
    origem: 'DTP',
    tipo: 'QUESTIONAMENTO_OS',
    titulo: 'Horário de saída divergente na OS 3145',
    justificativa:
      'O horário de saída informado pelo transportador na OS 3145 difere do horário da unidade. Pedimos que a SME confirme qual vale.',
    dreId: 'dre-9',
    status: 'RESPONDIDA_DRE',
    criadaHa: 8,
    prazoEmDias: 2,
    encaminhamentoDre: 'Confirmem o horário de saída praticado pela unidade.',
    respostaDre:
      'A unidade confirmou saída às 12h10 desde o início do semestre, por ajuste de grade. O horário anterior está desatualizado.',
    eventos: [
      { acao: 'ANALISAR', ha: 7 },
      { acao: 'ENCAMINHAR_DRE', ha: 6 },
      { acao: 'RESPONDER_SME', ha: 2 },
    ],
  },
  {
    id: 'sol-007',
    numero: 418,
    origem: 'SME',
    tipo: 'TROCA_VEICULO',
    titulo: 'Substituição de veículo com ar-condicionado inoperante',
    justificativa:
      'A unidade relata que o veículo da rota da tarde está sem climatização há três semanas. Solicitamos avaliação de substituição.',
    dreId: 'dre-5',
    escolaId: 'esc-dre-5-c',
    status: 'ATENDIDA',
    criadaHa: 20,
    prazoEmDias: -12,
    respostaFinal: 'Veículo substituído pelo transportador; vistoria do substituto aprovada.',
    eventos: [
      { acao: 'ANALISAR', ha: 19 },
      { acao: 'ATENDER', ha: 14, detalhe: 'Substituição confirmada após vistoria.' },
    ],
  },
  {
    id: 'sol-008',
    numero: 419,
    origem: 'DTP',
    tipo: 'OUTROS',
    titulo: 'Esclarecimento sobre reajuste da tabela de preços',
    justificativa:
      'Precisamos que a SME confirme se o reajuste anual já foi comunicado às DREs antes de aplicarmos a nova tabela na apuração.',
    dreId: 'dre-1',
    status: 'RECUSADA',
    criadaHa: 30,
    respostaFinal: 'Assunto de competência da DTP; não cabe manifestação da SME.',
    eventos: [
      { acao: 'ANALISAR', ha: 29 },
      { acao: 'RECUSAR', ha: 27, detalhe: 'Assunto de competência da DTP.' },
    ],
  },
  {
    id: 'sol-009',
    numero: 420,
    origem: 'SME',
    tipo: 'NOVA_ROTA',
    titulo: 'Nova rota para atender conjunto habitacional recém-entregue',
    justificativa:
      'Um conjunto habitacional entregue no mês passado concentra cerca de quarenta estudantes sem transporte. Pedimos estudo de nova rota.',
    dreId: 'dre-7',
    escolaId: 'esc-dre-7-a',
    status: 'ABERTA',
    criadaHa: 2,
    prazoEmDias: 12,
  },
  {
    id: 'sol-010',
    numero: 421,
    origem: 'SME',
    tipo: 'EXCLUSAO_ALUNO',
    titulo: 'Exclusão de cinco alunos por transferência de unidade',
    justificativa:
      'Cinco estudantes foram transferidos para outra unidade da mesma DRE. Pedimos a exclusão da rota atual para evitar pagamento indevido.',
    dreId: 'dre-9',
    escolaId: 'esc-dre-9-b',
    status: 'CANCELADA',
    criadaHa: 15,
    eventos: [{ acao: 'CANCELAR', ha: 14 }],
  },
  {
    id: 'sol-011',
    numero: 422,
    origem: 'DTP',
    tipo: 'REVISAO_BANCADA_VEICULO',
    titulo: 'Revisão de bancada — veículo com laudo pendente',
    justificativa:
      'O laudo de bancada de um veículo da DRE venceu e o transportador alega ter feito a revisão. Pedimos confirmação da SME junto à unidade.',
    dreId: 'dre-12',
    status: 'EM_ANALISE',
    criadaHa: 4,
    prazoEmDias: 6,
    eventos: [{ acao: 'ANALISAR', ha: 3 }],
  },
  {
    id: 'sol-012',
    numero: 423,
    origem: 'SME',
    tipo: 'TROCA_CONDUTOR',
    titulo: 'Troca de condutor após reclamação formal da unidade',
    justificativa:
      'A unidade registrou reclamação formal sobre a conduta do condutor da rota da manhã. Solicitamos que a DTP avalie a substituição.',
    dreId: 'dre-3',
    escolaId: 'esc-dre-3-a',
    status: 'EM_ANALISE',
    criadaHa: 7,
    prazoEmDias: -1,
    eventos: [{ acao: 'ANALISAR', ha: 6 }],
  },
];

function montar(s: Semente): SolicitacaoSmeDetalhe {
  const criadaEm = diasAtras(s.criadaHa);
  const eventos: EventoSolicitacaoSme[] = [
    { id: `${s.id}-ev-0`, acao: 'CRIAR', criadoEm: criadaEm, detalhe: null },
    ...(s.eventos ?? []).map((e, i) => ({
      id: `${s.id}-ev-${i + 1}`,
      acao: e.acao,
      criadoEm: diasAtras(e.ha),
      detalhe: e.detalhe ?? null,
    })),
  ];
  const ultimo = eventos[eventos.length - 1]!;
  const encaminhamento = eventos.find((e) => e.acao === 'ENCAMINHAR_DRE');
  const respostaDre = eventos.find((e) => e.acao === 'RESPONDER_SME');
  const escola = s.escolaId ? ESCOLAS.find((e) => e.id === s.escolaId) : undefined;
  return {
    id: s.id,
    numero: s.numero,
    origem: s.origem,
    tipo: s.tipo,
    titulo: s.titulo,
    dreId: s.dreId,
    dreNome: nomeDaDre(s.dreId),
    escolaId: escola?.id ?? null,
    escolaNome: escola?.nome ?? null,
    status: s.status,
    criadaEm,
    prazoDesejado: s.prazoEmDias === undefined ? null : dataDaqui(s.prazoEmDias),
    justificativa: s.justificativa,
    atualizadaEm: ultimo.criadoEm,
    atendidaEm: s.status === 'ATENDIDA' ? ultimo.criadoEm : null,
    encaminhamentoDre: s.encaminhamentoDre ?? null,
    encaminhadaDreEm: encaminhamento?.criadoEm ?? null,
    respostaDre: s.respostaDre ?? null,
    respostaDreEm: respostaDre?.criadoEm ?? null,
    respostaFinal: s.respostaFinal ?? null,
    eventos,
  };
}

/** Recalculado a cada chamada: as datas acompanham o relógio. */
function solicitacoesSimuladas(): SolicitacaoSmeDetalhe[] {
  return SEMENTES.map(montar);
}

function paraLinha(d: SolicitacaoSmeDetalhe): SolicitacaoSmeLinha {
  return {
    id: d.id,
    numero: d.numero,
    origem: d.origem,
    tipo: d.tipo,
    titulo: d.titulo,
    dreId: d.dreId,
    dreNome: d.dreNome,
    escolaId: d.escolaId,
    escolaNome: d.escolaNome,
    status: d.status,
    criadaEm: d.criadaEm,
    prazoDesejado: d.prazoDesejado,
  };
}

/** Mesma semântica da consulta do protótipo (`useSmeDemandas`): filtros no servidor. */
export function filtrarSolicitacoes(
  todas: SolicitacaoSmeLinha[],
  filtro: FiltroSolicitacoesSme,
): SolicitacaoSmeLinha[] {
  return todas
    .filter((s) => !filtro.status || s.status === filtro.status)
    .filter((s) => !filtro.origem || s.origem === filtro.origem)
    .filter((s) => !filtro.tipo || s.tipo === filtro.tipo)
    .filter((s) => !filtro.dreIds?.length || filtro.dreIds.includes(s.dreId))
    .filter((s) => !filtro.criadaDe || s.criadaEm.slice(0, 10) >= filtro.criadaDe)
    .filter((s) => !filtro.criadaAte || s.criadaEm.slice(0, 10) <= filtro.criadaAte)
    .filter((s) => {
      const termo = filtro.busca?.trim();
      if (!termo) return true;
      const numero = Number(termo.replace(/\D/g, ''));
      return (
        s.titulo.toLowerCase().includes(termo.toLowerCase()) || (numero > 0 && s.numero === numero)
      );
    })
    .sort((a, b) => b.criadaEm.localeCompare(a.criadaEm));
}

export function useSolicitacoesSme(
  filtro: FiltroSolicitacoesSme,
  opcoes: { habilitado?: boolean } = {},
) {
  return useQuery({
    queryKey: ['sme', 'demandas', filtro],
    queryFn: async () => filtrarSolicitacoes(solicitacoesSimuladas().map(paraLinha), filtro),
    enabled: opcoes.habilitado ?? true,
  });
}

export function useSolicitacaoSme(id: string, opcoes: { habilitado?: boolean } = {}) {
  return useQuery({
    queryKey: ['sme', 'demanda', id],
    queryFn: async () => solicitacoesSimuladas().find((s) => s.id === id) ?? null,
    enabled: (opcoes.habilitado ?? true) && id.length > 0,
  });
}

/** Escolas ativas de uma DRE, para o seletor do formulário. */
export function useEscolasDaDre(dreId: string | null) {
  return useQuery({
    queryKey: ['sme', 'escolas-da-dre', dreId],
    queryFn: async () => ESCOLAS.filter((e) => DRE_DA_ESCOLA.get(e.id) === dreId),
    enabled: Boolean(dreId),
    staleTime: 5 * 60_000,
  });
}

export interface ModeloDeSolicitacao {
  id: string;
  origem: OrigemSolicitacaoSme;
  nome: string;
  tipo: TipoSolicitacaoSme;
  tituloSugerido: string;
  justificativaSugerida: string;
}

/**
 * Modelos prontos do formulário (`sme_solicitacao_templates` no protótipo,
 * filtrados por origem). Só a DTP tem modelos cadastrados; sem modelo, o
 * cartão "Usar modelo pronto" não aparece — como no protótipo.
 */
const MODELOS: ModeloDeSolicitacao[] = [
  {
    id: 'mod-1',
    origem: 'DTP',
    nome: 'Cobrança de apontamento mensal pendente',
    tipo: 'ESCALONAMENTO_APONTAMENTO',
    tituloSugerido: 'Apontamento pendente — competência [MM/AAAA]',
    justificativaSugerida:
      'A DRE ainda não concluiu o apontamento da competência [MM/AAAA]. Solicitamos retorno para viabilizar o fechamento no prazo.',
  },
  {
    id: 'mod-2',
    origem: 'DTP',
    nome: 'Divergência em medição — solicitação de revisão',
    tipo: 'OUTROS',
    tituloSugerido: 'Divergência em medição — competência [MM/AAAA]',
    justificativaSugerida:
      'Identificada divergência nos valores apurados na medição da competência [MM/AAAA]. Solicitamos revisão antes do fechamento.',
  },
  {
    id: 'mod-3',
    origem: 'DTP',
    nome: 'Falta de documento obrigatório no credenciado',
    tipo: 'OUTROS',
    tituloSugerido: 'Documento pendente — credenciado [nome]',
    justificativaSugerida:
      'O credenciado [nome] está com documento obrigatório pendente de envio, impactando a apuração da competência.',
  },
  {
    id: 'mod-4',
    origem: 'DTP',
    nome: 'Esclarecimento sobre OS específica',
    tipo: 'QUESTIONAMENTO_OS',
    tituloSugerido: 'Esclarecimento — OS [número]',
    justificativaSugerida:
      'Necessário esclarecimento da SME/DRE sobre a OS [número] antes de prosseguir.',
  },
];

export function useModelosDeSolicitacao(origem: OrigemSolicitacaoSme) {
  return useQuery({
    queryKey: ['sme', 'modelos-de-solicitacao', origem],
    queryFn: async () => MODELOS.filter((m) => m.origem === origem),
    staleTime: 5 * 60_000,
  });
}
